import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Bell, X } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const POLL_MS = 20000;

export default function NotificationBell({ dark = false }) {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const lastCount = useRef(0);
  const timerRef = useRef(null);
  const { user } = useAuth();

  const fetchNotes = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotes(data);
      const unread = data.filter(n => !n.read);
      // Trigger browser notification for genuinely new items
      if (unread.length > lastCount.current && document.visibilityState === "visible") {
        const fresh = unread[0];
        if (fresh && "Notification" in window && Notification.permission === "granted") {
          try {
            new Notification(fresh.title, { body: fresh.body, tag: fresh.id });
          } catch (err) { console.warn("Browser Notification API failed", err); }
        }
      }
      lastCount.current = unread.length;
    } catch (err) { console.warn("fetch notifications failed", err); }
  };

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      // Ask for permission after a small delay so it doesn't disrupt the initial UI
      setTimeout(() => { try { Notification.requestPermission(); } catch (err) { console.warn("Notification.requestPermission failed", err); } }, 2000);
    }
    fetchNotes();
    timerRef.current = setInterval(fetchNotes, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  const unread = notes.filter(n => !n.read);
  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    fetchNotes();
  };
  const openItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    fetchNotes();
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)} data-testid="notification-bell"
        className={`relative h-10 w-10 flex items-center justify-center ${dark ? "bg-slate-800 hover:bg-slate-700" : "bg-white border-2 border-[#0f172a] hover:bg-slate-100"}`}>
        <Bell weight="bold" size={18}/>
        {unread.length > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 bg-[#ef4444] text-white text-[10px] font-black rounded-full flex items-center justify-center">
            {unread.length > 9 ? "9+" : unread.length}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed inset-0 z-[200] bg-black/30 backdrop-blur-[2px]" onClick={() => setOpen(false)} data-testid="notification-backdrop">
          <div className="absolute right-2 top-16 w-[min(90vw,340px)] max-h-[70vh] bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] overflow-hidden flex flex-col rounded-md"
            onClick={e => e.stopPropagation()} data-testid="notification-panel">
            <div className="p-3 flex items-center justify-between border-b-2 border-[#0f172a] bg-[#0f172a] text-white">
              <div className="font-display font-black uppercase tracking-wider text-sm">Notifications</div>
              <div className="flex items-center gap-2">
                {unread.length > 0 && (
                  <button onClick={markAllRead} data-testid="notification-mark-all-read"
                    className="text-xs font-black uppercase underline">Mark all read</button>
                )}
                <button onClick={() => setOpen(false)}><X weight="bold" size={16}/></button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1 divide-y bg-white">
              {notes.length === 0 && (
                <div className="p-6 text-center text-sm text-slate-500 font-medium">No notifications yet</div>
              )}
              {notes.map(n => {
                const rolePrefix = user?.role === "admin" ? "/admin/jobs/" : "/worker/jobs/";
                const to = n.link || (n.job_id ? `${rolePrefix}${n.job_id}` : "#");
                return (
                  <Link key={n.id} to={to} onClick={() => openItem(n)}
                    data-testid={`notification-item-${n.id}`}
                    className={`block p-3 ${!n.read ? "bg-cyan-50 hover:bg-cyan-100" : "bg-white hover:bg-slate-50"}`}>
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#00C4CC] flex-shrink-0"/>}
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm truncate">{n.title}</div>
                        <div className="text-xs text-slate-600 truncate">{n.body}</div>
                        <div className="text-[10px] text-slate-400 mt-1 uppercase font-bold tracking-wider">
                          {new Date(n.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
